import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import { Filter } from './types';

interface SingleSelectFilterProps {
  filter: Filter;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, filter: Filter) => void;
  disabled?: boolean;
  defaultText?: string;
  defaultValue?: string;
  showAllOption?: boolean;
  searchable?: boolean; // เพิ่ม prop ใหม่
}

const SingleSelectFilter: React.FC<SingleSelectFilterProps> = ({
  filter,
  value,
  onChange,
  disabled = false,
  defaultText = "ทั้งหมด",
  defaultValue = "",
  showAllOption = true,
  searchable = false, // default เป็น false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  // สร้าง options ที่รวม default option
  const allOptions = useMemo(() => {
    const options = [...filter.options];
    if (showAllOption) {
      options.unshift({ label: defaultText, value: defaultValue });
    }
    return options;
  }, [filter.options, showAllOption, defaultText, defaultValue]);

  // หา selected option
  const selectedOption = useMemo(() => {
    return allOptions.find(option => option.value === value) || null;
  }, [allOptions, value]);

  // กรอง options ตาม input
  const filteredOptions = useMemo(() => {
    if (!inputValue) return allOptions;
    return allOptions.filter(option => 
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [allOptions, inputValue]);

  const handleChange = (_event: any, newValue: any) => {
    // สร้าง synthetic event เพื่อให้ compatible กับ onChange เดิม
    const syntheticEvent = {
      target: {
        value: newValue?.value || defaultValue
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent, filter);
  };

  const handleInputChange = (_event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  // รูปแบบเดิม (Select)
  if (!searchable) {
    return (
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
        }}
      >
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 500, color: "#333" }}
        >
          {filter.label}
        </Typography>
        <FormControl>
          <Select
            key={`${filter.value}-select`}
            value={value}
            onChange={(e) => onChange(e as any, filter)}
            displayEmpty
            disabled={disabled}
            renderValue={(value) => {
              if (!value || value === defaultValue) {
                return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{defaultText}</span>;
              }
              const selectedOption = filter.options.find(option => option.value === value);
              return selectedOption?.label || filter.label;
            }}
          >
            {showAllOption && (
              <MenuItem value={defaultValue}>{defaultText}</MenuItem>
            )}
            {filter.options.map((option) => (
              <MenuItem key={`${filter.value}-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  // รูปแบบใหม่ (Autocomplete with search)
  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
      }}
    >
      <Typography
        variant="body2"
        sx={{ mb: 1, fontWeight: 500, color: "#333" }}
      >
        {filter.label}
      </Typography>
      <FormControl fullWidth>
        <Autocomplete
          value={selectedOption}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={filteredOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={defaultText}
              disabled={disabled}
              size="small"
            />
          )}
          open={open}
          onOpen={() => {
            setOpen(true);
            // เมื่อเปิด dropdown ให้แสดง input value เป็นค่าว่างเพื่อให้เห็น options ทั้งหมด
            if (selectedOption?.value === defaultValue) {
              setInputValue('');
            }
          }}
          onClose={() => {
            setOpen(false);
            // เมื่อปิด dropdown ให้ reset input value กลับไปเป็นค่าเดิม
            if (selectedOption?.value === defaultValue) {
              setInputValue('');
            }
          }}
          onFocus={() => {
            // เมื่อ focus ให้แสดง input value เป็นค่าว่างถ้าเลือก "ทั้งหมด"
            if (selectedOption?.value === defaultValue) {
              setInputValue('');
            }
          }}
          onBlur={() => {
            // เมื่อ blur ให้ reset input value กลับไปเป็นค่าเดิม
            if (selectedOption?.value === defaultValue) {
              setInputValue('');
            }
          }}
          filterOptions={(x) => x} // ปิดการ filter อัตโนมัติ เพราะเราจัดการเองแล้ว
          noOptionsText="ไม่พบข้อมูล"
          loadingText="กำลังโหลด..."
        />
      </FormControl>
    </Box>
  );
};

export default SingleSelectFilter; 